// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ReleaseStepType } from '../types';
import { RootState } from '../reducers/reducers';
import { getAllAppsInOrg, getAppGroupsForOrgId } from '../helpers';
import * as AnalyticEvents from '../utils/analytics';
import { allReleasePropertiesFrom } from './trackingHelpers';
import Button, { ButtonProps } from 'antd/lib/button';
import { selectOrg, selectApp, selectRelease } from '../selectors/selectors';
import { StringKeyedDictionary } from '../typescriptHelpers';

export interface TrackedButtonProps extends ButtonProps {
    children: React.ReactNode;
    eventName?: AnalyticEvents.Event;
    orgId?: string; // pass orgId to send orgProperties with event
    appId?: string; // pass appId to send appProperties with event
    releaseId?: string; // pass releaseId to send releaseProperties with event
    context?: AnalyticEvents.Context;
    releaseStepType?: ReleaseStepType; // pass releaseStepType to send with event
    extraProperties?: StringKeyedDictionary<unknown>;
}

export const TrackedButton = (props: TrackedButtonProps) => {
    const { eventName, orgId, appId, releaseId, children, extraProperties, ...rest } = props;
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => {
        if (appId) {
            return selectApp(state, appId);
        }

        return null;
    });
    const release = useSelector((state: RootState) => {
        if (releaseId) {
            return selectRelease(state, releaseId);
        }

        return null;
    });
    const appsInOrg = useSelector((state: RootState) => {
        if (orgId) {
            return getAllAppsInOrg(orgId, state).map((app) => app.appId);
        }

        return null;
    });
    const appGroupsInOrg = useSelector((state: RootState) => {
        if (orgId) {
            return getAppGroupsForOrgId(orgId, state).map((appGroup) => appGroup.name);
        }

        return null;
    });

    const properties = allReleasePropertiesFrom(
        org,
        app,
        release,
        appsInOrg,
        appGroupsInOrg,
        props.context,
        props.releaseStepType,
    );

    let allProperties = {};

    if (extraProperties) {
        allProperties = {
            ...properties,
            ...extraProperties,
        };
    } else {
        allProperties = {
            ...properties,
        };
    }

    const trackOnClick = (e) => {
        const trackedEventName = eventName ?? `click button`;

        if (!eventName && typeof children === 'string') {
            allProperties['cta'] = children;
        }

        AnalyticEvents.track(trackedEventName, allProperties);

        if (props.onClick) {
            props.onClick(e);
        }
    };

    return (
        <Button {...rest} onClick={trackOnClick}>
            {children}
        </Button>
    );
};

export default TrackedButton;
